import type { PatchConfig, Size, SizePreset, TextLine, TextSizeTier } from '@cowell/shared';
import { maxLinesFor, TEXT_RULES } from '@cowell/shared';
import { create } from 'zustand';

import { track } from '../analytics/index.js';
import { DEFAULT_CONFIG } from './defaults.js';

type LinePatch = {
  readonly text?: string;
  readonly size?: TextSizeTier;
};

interface State {
  config: PatchConfig;
  applyPreset(preset: SizePreset): void;
  applyCustomSize(width: number, height: number): void;
  setVinylColor(id: string): void;
  setPlacardColor(id: string): void;
  setQuantity(q: number): void;
  updateLine(index: number, patch: LinePatch): void;
  addLine(): void;
  removeLine(index: number): void;
  reset(): void;
}

function sanitizeLineText(raw: string): string {
  // Uppercase, strip disallowed characters, clamp length.
  const upper = raw.toUpperCase();
  const stripped = Array.from(upper)
    .filter((ch) => TEXT_RULES.allowedChars.test(ch))
    .join('');
  return stripped.slice(0, TEXT_RULES.maxCharsPerLine);
}

export const useConfigStore = create<State>((set) => ({
  config: DEFAULT_CONFIG,

  applyPreset: (preset) =>
    set((s) => {
      track({ name: 'config_changed', field: 'size', value: preset });
      const size: Size = { width: preset.width, height: preset.height, isCustom: false };
      const max = maxLinesFor(size);
      const lines = s.config.lines.length > max ? s.config.lines.slice(0, max) : s.config.lines;
      return { config: { ...s.config, size, lines } };
    }),

  applyCustomSize: (width, height) =>
    set((s) => {
      track({ name: 'config_changed', field: 'size', value: { width, height, custom: true } });
      const size: Size = { width, height, isCustom: true };
      const max = maxLinesFor(size);
      const lines = s.config.lines.length > max ? s.config.lines.slice(0, max) : s.config.lines;
      return { config: { ...s.config, size, lines } };
    }),

  setVinylColor: (id) =>
    set((s) => {
      track({ name: 'config_changed', field: 'vinylColorId', value: id });
      return { config: { ...s.config, vinylColorId: id } };
    }),

  setPlacardColor: (id) =>
    set((s) => {
      track({ name: 'config_changed', field: 'placardColorId', value: id });
      return { config: { ...s.config, placardColorId: id } };
    }),

  setQuantity: (q) =>
    set((s) => {
      const clamped = Math.max(1, Math.min(10_000, Math.floor(q) || 1));
      track({ name: 'config_changed', field: 'quantity', value: clamped });
      return { config: { ...s.config, quantity: clamped } };
    }),

  updateLine: (index, patch) =>
    set((s) => {
      const next: TextLine[] = s.config.lines.map((line, i) => {
        if (i !== index) return line;
        const text = patch.text !== undefined ? sanitizeLineText(patch.text) : line.text;
        const size = patch.size ?? line.size;
        return { text, size };
      });
      track({ name: 'config_changed', field: `lines.${index}`, value: patch });
      return { config: { ...s.config, lines: next } };
    }),

  addLine: () =>
    set((s) => {
      const max = Math.min(TEXT_RULES.maxLines, maxLinesFor(s.config.size));
      if (s.config.lines.length >= max) return s;
      const next: TextLine[] = [...s.config.lines, { text: '', size: 'large' }];
      track({ name: 'config_changed', field: 'lines.add', value: next.length });
      return { config: { ...s.config, lines: next } };
    }),

  removeLine: (index) =>
    set((s) => {
      if (s.config.lines.length <= TEXT_RULES.minLines) return s;
      const next = s.config.lines.filter((_, i) => i !== index);
      track({ name: 'config_changed', field: 'lines.remove', value: index });
      return { config: { ...s.config, lines: next } };
    }),

  reset: () => set({ config: DEFAULT_CONFIG }),
}));
